import { useTranslate } from '@tolgee/react';
import { HTMLAttributes, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getVideoThumbUrl } from 'utils/validator/video';
import useRevokeObjectURL from 'hooks/useRevokeObjectURL';
import dayjs from 'lib/dayjs';
import posthog from '@leyan/lytics';
import { noop } from 'lodash-es';
import wait from 'utils/wait';
import chatttSDK from 'services/chattt';
import { getFacebookMessage, getTiktokNormalQualityVideo } from 'services/api/conversation';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import styled from '@emotion/styled';
import { ExclamationCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import SafeImage from '../../../../SafeImage';
import { VideoModal } from './VideoModal';
import { getMsgById } from 'services/api/conversation.convList';
import { DialogueModeContext } from 'views/Conversation/Chat/components/DialogueModeContext.tsx';
import { useSafeState } from 'ahooks';
import { useUpdateMessage } from 'hooks/business/message';

export interface VideoPreviewerProps extends HTMLAttributes<HTMLDivElement> {
  url: string;
  localVideoUrl?: string;
  thumb?: string;
  expireTimestamp?: string;
  msg?: Conversation.Message;
  onVideoUrlUpdate?: (url: string) => void;
  onVisibleChange?: (visible: boolean) => void;
  controls?: boolean;
  channel?: Conversation.Message['channel'];
  onOpenVideoPlayer?: () => void;
  lazyScrollContainer?: Element | null;
}

export const VideoPreviewer = (props: VideoPreviewerProps) => {
  const {
    url,
    thumb,
    channel,
    onOpenVideoPlayer,
    controls = true,
    onVisibleChange,
    expireTimestamp,
    msg,
    onVideoUrlUpdate,
    lazyScrollContainer,
    localVideoUrl,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const isFaq = useContext(DialogueModeContext) === 'faq';

  const [visible, setVisible] = useState(false);
  const [thumbUrl, setThumbUrl] = useSafeState(thumb);

  useEffect(() => {
    if (thumb) {
      return;
    }

    getVideoThumbUrl(url)
      .then(setThumbUrl)
      .catch(() => {});
  }, [url, thumb, setThumbUrl]);

  useRevokeObjectURL(thumbUrl || '');

  const videoPlaceHolder = useMemo(() => {
    return <Placeholder />;
  }, []);

  const handleVisibleChange = (visible: boolean) => {
    setVisible(visible);

    if (onVisibleChange) {
      onVisibleChange(visible);
    }
  };

  const isExpired = useMemo(() => {
    const time = expireTimestamp ? Number(expireTimestamp) : 0;
    return time ? dayjs().isAfter(dayjs.unix(Number(time))) : false;
  }, [expireTimestamp]);

  const updateMessage = useUpdateMessage();
  const openningRef = useRef(false);

  return (
    <Wrapper {...otherProps}>
      <Box
        onClick={async () => {
          if (isExpired || openningRef.current) return;

          openningRef.current = true;

          const isTiktok = isTiKTokUpperCase(channel);

          if (isTiktok && msg && msg?.videoContent?.quality !== 'normal') {
            await getTiktokNormalQualityVideo({
              conversationId: msg.conversationId,
              msgId: msg.msgId,
            })
              .then((res) => {
                posthog.capture(
                  res.quality === 'normal'
                    ? '$tiktokVideoQualityNormal'
                    : '$tiktokVideoQualityOriginal',
                );
                updateMessage(msg.msgId, {
                  ...msg,
                  videoContent: res,
                });
                onVideoUrlUpdate?.(res.videoUrl);
              })
              .catch(noop);
            await wait(50);
          }

          if (channel === 'SHOPEE' && !isFaq && msg?.msgId) {
            await getMsgById(msg.msgId)
              .then(({ videoContent }) => {
                updateMessage(msg.msgId, {
                  ...msg,
                  videoContent,
                });
                if (videoContent?.videoUrl) {
                  onVideoUrlUpdate?.(videoContent.videoUrl);
                }
              })
              .catch(noop);
            await wait(50);
          }

          if (
            channel === 'FACEBOOK' &&
            msg?.fbMsgContent?.expireTimestamp &&
            Number(msg.fbMsgContent.expireTimestamp) > 0 &&
            Date.now() >= Number(msg.fbMsgContent.expireTimestamp)
          ) {
            await getFacebookMessage({ storeId: msg.storeId, msgId: msg.msgId })
              .then(({ fbMsgContent }) => {
                updateMessage(msg.msgId, {
                  ...msg,
                  fbMsgContent,
                });
                if (fbMsgContent?.attachments?.data?.[0]?.videoData?.url) {
                  onVideoUrlUpdate?.(fbMsgContent?.attachments?.data?.[0]?.videoData?.url);
                }
              })
              .catch(noop);
            await wait(50);
          }

          openningRef.current = false;

          if (isTiktok && chatttSDK?.videoPlayer) {
            // TikTok 部分视频资源是 Hevc 编码，在 App 内无法播放
            // 通过乐聊内置的视频播放器转码后播放
            onOpenVideoPlayer?.();
            return;
          }
          setVisible(true);
          handleVisibleChange(true);
        }}
      >
        <Video
          reload={false}
          spin={videoPlaceHolder}
          placeholder={videoPlaceHolder}
          src={thumbUrl}
          alt="video"
          target={lazyScrollContainer}
        />
        {isExpired ? (
          <Mask>
            <WarningIcon />
            <WarningText>{t('ViewsChat.PLATFORM_EXPIRED')}</WarningText>
          </Mask>
        ) : (
          <PlayIconBox>
            <PlayCircleOutlined />
          </PlayIconBox>
        )}
      </Box>
      <VideoModal
        localVideoUrl={localVideoUrl}
        visible={visible}
        url={url}
        controls={controls}
        channel={channel}
        onCancel={(e) => {
          e.stopPropagation();
          setVisible(false);
          handleVisibleChange(false);
        }}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 340px;
  height: 192px;
`;
const Box = styled.div`
  position: relative;
  cursor: pointer;
`;
const Placeholder = styled.div`
  width: 100%;
  height: 180px;
  background: #000;
`;
const PlayIconBox = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  color: #fff;
  font-size: 40px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;
const Video = styled(SafeImage)`
  width: 340px;
  background-color: rgba(0, 0, 0, 0.8);
  aspect-ratio: 16/9;
  object-fit: cover;
`;
const Mask = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1;
`;
const WarningIcon = styled(ExclamationCircleOutlined)`
  font-size: 40px;
  color: #fff;
`;
const WarningText = styled.div`
  color: #fff;
  margin-top: 10px;
`;
